import { createFileRoute } from '@tanstack/react-router';

import { buildGuards, hasFlagEnabled } from '@mint-lib/routing-guards';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';

import { PendingComponent } from '@myn/import-hub/routes';

const dataFilePreloadGuard = buildGuards(hasFlagEnabled('dataFilePreload'));
const importHubV2Guard = buildGuards(hasFlagEnabled('importHubV2'));
const simplifiedHeadersMetaGuard = buildGuards(
  hasFlagEnabled('simplifiedHeadersMeta'),
);

export const Route = createFileRoute(
  '/_secured/external-data/import-hub/performance/imports/$dataFileId/headers-mapping',
)({
  staleTime: 30 * 1000,
  loader: async ({ context, params, cause }) => {
    const armGuardsContext = await context.getGuardContext(
      await context.getInstance('flagsmith', '@mint/arm'),
    );

    if (!dataFilePreloadGuard(armGuardsContext) || cause !== 'preload') {
      return;
    }

    const isImportHubV2 = importHubV2Guard(armGuardsContext);
    const isSimplifiedHeadersMeta =
      simplifiedHeadersMetaGuard(armGuardsContext);

    const queryClient =
      await context.serviceLocator.getOrThrowInstance('queryClient');

    const dataFilePreviewV1QueryOptions =
      DataIngestion.DataFile.getTableDataFilePreviewV1QueryOptions({
        urlParams: { id: params.dataFileId },
      });

    const dataFilePreviewV2QueryOptions =
      DataIngestion.DataFile.getTableDataFilePreviewV2QueryOptions({
        urlParams: { id: params.dataFileId },
      });

    const dataFilePreview = isImportHubV2
      ? await queryClient.ensureQueryData(dataFilePreviewV2QueryOptions)
      : await queryClient.ensureQueryData(dataFilePreviewV1QueryOptions);

    void queryClient.prefetchQuery(
      DataIngestion.DataFile.getDataFileFormatsQueryOptions({
        urlParams: { id: params.dataFileId },
      }),
    );

    void queryClient.prefetchQuery(
      DataIngestion.DataFile.getHeadersMappingQueryOptions({
        urlParams: { id: params.dataFileId },
      }),
    );

    const systemMetricsParams = {
      granularityLevelType: dataFilePreview.granularityLevel,
    };
    const systemMetricsUrlParams = { id: params.dataFileId };

    if (isImportHubV2) {
      if (isSimplifiedHeadersMeta) {
        void queryClient.prefetchQuery(
          DataIngestion.SystemMetrics.getSimplifiedDataFileSystemMetricsQueryOptions(
            { urlParams: systemMetricsUrlParams, params: systemMetricsParams },
          ),
        );
      } else {
        void queryClient.prefetchQuery(
          DataIngestion.SystemMetrics.getDataFileSystemMetricsQueryOptions({
            urlParams: systemMetricsUrlParams,
            params: systemMetricsParams,
          }),
        );
      }
      return;
    }

    if (isSimplifiedHeadersMeta) {
      void queryClient.prefetchQuery(
        DataIngestion.SystemMetrics.getSimplifiedSystemMetricsQueryOptions({
          params: systemMetricsParams,
        }),
      );
    } else {
      void queryClient.prefetchQuery(
        DataIngestion.SystemMetrics.getSystemMetricsQueryOptions({
          params: systemMetricsParams,
        }),
      );
    }
  },
  pendingComponent: PendingComponent,
});
